import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { underwriting } from '../services/api.js';
import { UnderwriterChat } from '../components/UnderwriterChat.jsx';
import './UnderwritingCanvas.css';

function getRiskBadgeClass(tierOrScore) {
  if (tierOrScore == null) return 'badge';
  if (typeof tierOrScore === 'string') {
    if (tierOrScore === 'unscored') return 'badge';
    return `badge badge--${tierOrScore}`;
  }
  if (tierOrScore <= 2) return 'badge badge--red';
  if (tierOrScore <= 5) return 'badge badge--orange';
  if (tierOrScore <= 8) return 'badge badge--yellow';
  return 'badge badge--green';
}

function getRiskLabel(tierOrScore) {
  if (tierOrScore == null) return 'Pending';
  if (typeof tierOrScore === 'string') {
    const labels = { red: 'High Risk', orange: 'Elevated', yellow: 'Moderate', green: 'Low Risk', unscored: 'Pending' };
    return labels[tierOrScore] || tierOrScore;
  }
  if (tierOrScore <= 2) return 'High Risk';
  if (tierOrScore <= 5) return 'Elevated';
  if (tierOrScore <= 8) return 'Moderate';
  return 'Low Risk';
}

function getConfidenceDot(confidence) {
  if (confidence == null) return null;
  const pct = typeof confidence === 'string' ? parseFloat(confidence) : confidence;
  let color = 'var(--color-risk-green)';
  if (pct < 0.5) color = 'var(--color-risk-red)';
  else if (pct < 0.75) color = 'var(--color-risk-orange)';
  else if (pct < 0.9) color = 'var(--color-risk-yellow)';
  return (
    <span
      className="uc__confidence-dot"
      style={{ backgroundColor: color }}
      title={`Confidence: ${Math.round(pct * 100)}%`}
    />
  );
}

function formatKey(key) {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const CURRENCY_KEYS = /revenue|premium|limit|deductible|tiv|valuation|amount|price|cost|wage|salary|income|retention/i;

function formatValue(key, value) {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  if (typeof value === 'number' && CURRENCY_KEYS.test(key)) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function formatCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

export function UnderwritingCanvas() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [canvas, setCanvas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Risk assessment form
  const [notes, setNotes] = useState('');
  const [expandedDrivers, setExpandedDrivers] = useState({});
  const [deciding, setDeciding] = useState(false);
  const [decisionError, setDecisionError] = useState(null);
  const [decisionMade, setDecisionMade] = useState(null);

  const loadCanvas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await underwriting.getCanvas(submissionId);
      const canvasData = result.data || result;
      setCanvas(canvasData);

      // Load existing notes/decision from the assessment
      const assessment = canvasData.riskAssessment || canvasData.Assessment || {};
      setNotes(assessment.Notes || '');

      if (assessment.Decision) {
        setDecisionMade({
          decision: assessment.Decision,
          rationale: assessment.DecisionRationale || assessment.Notes || '',
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to load underwriting canvas');
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => {
    loadCanvas();
  }, [loadCanvas]);

  const toggleDriver = (idx) => {
    setExpandedDrivers((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleDecision = async (decision) => {
    setDeciding(true);
    setDecisionError(null);
    try {
      await underwriting.makeDecision(submissionId, {
        decision,
        rationale: notes,
      });
      setDecisionMade({ decision, rationale: notes });
    } catch (err) {
      setDecisionError(err.message || 'Decision failed');
    } finally {
      setDeciding(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      await underwriting.updateAssessment(submissionId, { Notes: notes });
    } catch {
      // Silent save
    }
  };

  if (loading) {
    return (
      <div className="uc-loading">
        <div className="spinner" />
        <p>Loading underwriting canvas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="uc-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button className="btn btn--primary" onClick={loadCanvas}>
          Retry
        </button>
      </div>
    );
  }

  if (!canvas) return null;

  // Extract entities – API returns lowercase keys for nested objects
  const sub = canvas.submission || canvas.Submission || {};
  const account = canvas.account || canvas.Account || {};
  const assessment = canvas.riskAssessment || canvas.Assessment || null;
  const product = canvas.product || canvas.Product || {};
  const insured = canvas.insured || canvas.Insured || {};

  // Top-level display names
  const insuredName = canvas.InsuredName || insured.Name || account.InsuredName || sub.InsuredName || 'Submission';
  const productName = canvas.ProductName || product.Name || sub.ProductID || '—';
  const brokerName = canvas.BrokerName || sub.BrokerID || '—';
  const assignedUWName = canvas.AssignedUWName || sub.AssignedUW || '—';
  const uwGuidelines = canvas.underwritingGuidelines || product.UnderwritingGuidelines || '';

  const structuredData = sub.StructuredData || {};
  const riskDrivers = assessment && Array.isArray(assessment.RiskDrivers) ? assessment.RiskDrivers : [];
  const disqualifiers = assessment && Array.isArray(assessment.Disqualifiers) ? assessment.Disqualifiers : [];
  const missingFlags = Array.isArray(sub.MissingData) ? sub.MissingData : [];
  const riskScore = assessment?.RiskScore ?? sub.RiskScore;
  const riskTier = assessment?.RiskTier || sub.RiskTier;
  const hasAssessment = assessment != null && (riskScore != null || riskDrivers.length > 0);
  const existingDecision = decisionMade || (assessment?.Decision ? { decision: assessment.Decision, rationale: assessment.DecisionRationale } : null);
  const subStatus = sub.Status || sub.status || '';
  const isScoringInProgress = subStatus === 'data_extracted' || subStatus === 'new';

  return (
    <div className="uc">
      {/* Top bar */}
      <div className="uc__topbar">
        <div className="uc__topbar-left">
          <button
            className="sd__back"
            onClick={() => navigate(`/submissions/${submissionId}`)}
          >
            &larr; Back
          </button>
          <h2 className="uc__topbar-insured">{insuredName}</h2>
          <span className={getRiskBadgeClass(riskTier || riskScore)}>
            {riskScore != null
              ? `${riskScore} · ${getRiskLabel(riskTier || riskScore)}`
              : 'Pending'}
          </span>
        </div>
        <div className="uc__topbar-right">
          <span className="uc__topbar-meta">{productName}</span>
          <span className="uc__topbar-meta">Broker: {brokerName}</span>
          <span className="uc__topbar-meta">UW: {assignedUWName}</span>
          {(account.AccountID || sub.AccountID) && (
            <Link
              to={`/accounts/${account.AccountID || sub.AccountID}`}
              className="uc__topbar-link"
            >
              View Account
            </Link>
          )}
        </div>
      </div>

      {/* Three-panel layout */}
      <div className="uc__panels">
        {/* Left panel: Structured data + Insured details */}
        <div className="uc__panel uc__panel--left">
          <h3 className="uc__panel-title">Submission Data</h3>

          {/* Insured details */}
          {(insured.Name || insured.Industry || insured.Revenue != null) && (
            <div className="uc__insured-section">
              <h4 className="uc__sub-title">Insured Details</h4>
              {insured.Industry && (
                <div className="uc__data-row">
                  <div className="uc__data-label">Industry</div>
                  <div className="uc__data-value">{insured.Industry}</div>
                </div>
              )}
              {insured.Revenue != null && (
                <div className="uc__data-row">
                  <div className="uc__data-label">Revenue</div>
                  <div className="uc__data-value">{formatCurrency(insured.Revenue)}</div>
                </div>
              )}
              {insured.EmployeeCount != null && (
                <div className="uc__data-row">
                  <div className="uc__data-label">Employees</div>
                  <div className="uc__data-value">{insured.EmployeeCount.toLocaleString()}</div>
                </div>
              )}
              {insured.Website && (
                <div className="uc__data-row">
                  <div className="uc__data-label">Website</div>
                  <div className="uc__data-value">{insured.Website}</div>
                </div>
              )}
              {insured.Attested != null && (
                <div className="uc__data-row">
                  <div className="uc__data-label">Attested</div>
                  <div className="uc__data-value">{insured.Attested ? 'Yes' : 'No'}</div>
                </div>
              )}
            </div>
          )}

          {/* Structured data from submission */}
          <div className="uc__data-list">
            {structuredData && Object.keys(structuredData).length > 0 ? (
              Object.entries(structuredData).map(([key, entry]) => {
                const value =
                  typeof entry === 'object' && entry?.value !== undefined
                    ? entry.value
                    : entry;
                const confidence =
                  typeof entry === 'object' ? entry?.confidence || entry?.Confidence : null;
                return (
                  <div key={key} className="uc__data-row">
                    <div className="uc__data-label">
                      {getConfidenceDot(confidence)}
                      {formatKey(key)}
                    </div>
                    <div className="uc__data-value">
                      {formatValue(key, value)}
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="uc__empty">No structured data available.</p>
            )}
          </div>
        </div>

        {/* Center panel: Risk assessment */}
        <div className="uc__panel uc__panel--center">
          <h3 className="uc__panel-title">Risk Assessment</h3>

          {hasAssessment ? (
            <>
              {/* Risk score display */}
              <div className="uc__score-display">
                <div
                  className="uc__score-circle"
                  data-tier={
                    riskTier ||
                    (riskScore == null
                      ? 'none'
                      : riskScore <= 2
                        ? 'red'
                        : riskScore <= 5
                          ? 'orange'
                          : riskScore <= 8
                            ? 'yellow'
                            : 'green')
                  }
                >
                  <span className="uc__score-value">{riskScore ?? '—'}</span>
                  <span className="uc__score-label">
                    {getRiskLabel(riskTier || riskScore)}
                  </span>
                </div>
              </div>

              {/* Disqualifiers - red flagged, shown prominently */}
              {disqualifiers.length > 0 && (
                <div className="uc__disqualifiers">
                  <h4 className="uc__sub-title uc__sub-title--red">Disqualifiers</h4>
                  <ul className="uc__flag-list">
                    {disqualifiers.map((d, idx) => (
                      <li key={idx} className="uc__flag-item uc__flag-item--red">
                        <span className="uc__flag-icon uc__flag-icon--red">✕</span>
                        <span>{typeof d === 'string' ? d : d.Name || d.Reason || JSON.stringify(d)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Decision rationale from AI assessment (only if no human decision yet) */}
              {assessment.DecisionRationale && !existingDecision && (
                <div className="uc__rationale-block">
                  <h4 className="uc__sub-title">AI Rationale</h4>
                  <p className="uc__rationale-text">{assessment.DecisionRationale}</p>
                </div>
              )}

              {/* Risk drivers accordion */}
              <div className="uc__drivers">
                <h4 className="uc__sub-title">Risk Drivers</h4>
                {riskDrivers.length > 0 ? (
                  riskDrivers.map((driver, idx) => (
                    <div key={idx} className="uc__driver">
                      <button
                        className={`uc__driver-header ${expandedDrivers[idx] ? 'uc__driver-header--open' : ''}`}
                        onClick={() => toggleDriver(idx)}
                      >
                        <span className="uc__driver-name">
                          {getConfidenceDot(driver.Confidence)}
                          {driver.Name || `Driver ${idx + 1}`}
                        </span>
                        <span className="uc__driver-impact">
                          {driver.Impact || ''}
                          {driver.Score != null ? ` (${driver.Score})` : ''}
                        </span>
                        <span className="uc__driver-chevron">
                          {expandedDrivers[idx] ? '−' : '+'}
                        </span>
                      </button>
                      {expandedDrivers[idx] && (
                        <div className="uc__driver-detail">
                          <p>
                            {driver.Detail || 'No additional detail available.'}
                          </p>
                          {driver.Confidence != null && (
                            <span className="uc__driver-confidence">
                              Confidence:{' '}
                              {Math.round(
                                (typeof driver.Confidence === 'number'
                                  ? driver.Confidence
                                  : parseFloat(driver.Confidence)) * 100
                              )}
                              %
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="uc__empty">No risk drivers identified yet.</p>
                )}
              </div>
            </>
          ) : (
            <div className="uc__no-assessment">
              {isScoringInProgress ? (
                <>
                  <div className="uc__no-assessment-icon">&#9203;</div>
                  <p className="uc__no-assessment-text">AI risk scoring in progress...</p>
                  <p className="uc__no-assessment-hint">The risk assessment will appear here once processing is complete.</p>
                </>
              ) : (
                <>
                  <div className="uc__no-assessment-icon">&#128203;</div>
                  <p className="uc__no-assessment-text">No risk assessment yet</p>
                  <p className="uc__no-assessment-hint">A risk assessment has not been generated for this submission.</p>
                </>
              )}
            </div>
          )}

          {/* Decision success message */}
          {existingDecision && (
            <div className={`uc__decision-result uc__decision-result--${existingDecision.decision}`}>
              <h4 className="uc__decision-result-title">
                Decision: <span style={{ textTransform: 'capitalize' }}>{existingDecision.decision}</span>
              </h4>
              {existingDecision.rationale && (
                <p className="uc__decision-result-rationale">{existingDecision.rationale}</p>
              )}
            </div>
          )}

          {/* Notes + decision buttons (only if no decision yet and assessment exists) */}
          {!existingDecision && hasAssessment && (
            <>
              <div className="uc__notes">
                <h4 className="uc__sub-title">Underwriter Notes</h4>
                <textarea
                  className="uc__notes-textarea"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onBlur={handleSaveNotes}
                  placeholder="Add rationale notes before making a decision..."
                  rows={5}
                />
              </div>

              {decisionError && (
                <div className="uc__decision-error">{decisionError}</div>
              )}
              <div className="uc__decision-actions">
                <button
                  className="btn uc__btn-bind"
                  onClick={() => handleDecision('bind')}
                  disabled={deciding}
                >
                  {deciding ? 'Processing...' : 'Bind'}
                </button>
                <button
                  className="btn uc__btn-refer"
                  onClick={() => handleDecision('refer')}
                  disabled={deciding}
                >
                  {deciding ? 'Processing...' : 'Refer'}
                </button>
                <button
                  className="btn btn--danger"
                  onClick={() => handleDecision('decline')}
                  disabled={deciding}
                >
                  {deciding ? 'Processing...' : 'Decline'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Right panel: Context */}
        <div className="uc__panel uc__panel--right">
          {/* Missing data flags */}
          <div className="uc__context-section">
            <h3 className="uc__panel-title">Missing Data Flags</h3>
            {missingFlags.length > 0 ? (
              <ul className="uc__flag-list">
                {missingFlags.map((flag, idx) => (
                  <li key={idx} className="uc__flag-item">
                    <span className="uc__flag-icon">!</span>
                    <span>
                      {typeof flag === 'string'
                        ? flag
                        : flag.Field || flag.FieldName || flag.Name || JSON.stringify(flag)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="uc__empty-sm">No missing data flags.</p>
            )}
          </div>

          {/* Product underwriting guidelines */}
          {uwGuidelines && (
            <div className="uc__context-section">
              <h3 className="uc__panel-title">Underwriting Guidelines</h3>
              <div className="uc__guidelines-text">{uwGuidelines}</div>
            </div>
          )}

          {/* Similar past risks - placeholder */}
          <div className="uc__context-section">
            <h3 className="uc__panel-title">Similar Past Risks</h3>
            <p className="uc__empty-sm">Similar risk analysis coming soon.</p>
          </div>
        </div>
      </div>
      <UnderwriterChat submissionId={submissionId} />
    </div>
  );
}
